import React, { useEffect } from 'react';
import {
  View,
  StyleSheet,
  ActivityIndicator,
  Text,
  TouchableOpacity,
  Dimensions,
  FlatList,
  ListRenderItem,
} from 'react-native';
import { AdminEmptyState } from '@/components';
import { EmptyStateType } from '../mobile/AdminEmptyState';
import { TableFilters } from '../mobile/TableFilters';
import { TableRow } from '../mobile/TableRow';
import { DesktopDataTable } from '../desktop/dataTable/DesktopDataTable';
import { IDataTableProps } from '@/types/admin/dataTable';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useDataTableState } from '@/contexts/admin/DataTableContext';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';

const { width: screenWidth } = Dimensions.get('window');
const isTablet = screenWidth >= 768;

export const DataTable = <T,>(props: IDataTableProps<T>) => {
  const {
    data,
    columns,
    isLoading,
    searchQuery: searchQueryProp,
    onSearchChange: onSearchChangeProp,
    searchPlaceholder,
    showSearch = true,
    showSort = true,
    filterModel: filterModelProp,
    onFilterChange: onFilterChangeProp,
    sortModel: sortModelProp,
    onSortChange: onSortChangeProp,
    paginationModel: paginationModelProp,
    onPaginationChange: onPaginationChangeProp,
    hasNextPage: hasNextPageProp,
    hasPreviousPage: hasPreviousPageProp,
    onNextPage: onNextPageProp,
    onPreviousPage: onPreviousPageProp,
    onRowSelect,
    selectedRows = [],
    enableRowSelection = false,
    onSelectAll,
    onDeselectAll,
    updateCsvData,
    emptyStateType = 'residents',
    containerStyle,
    isTabletView = isTablet,
    mobileColumnCount = 2,
  } = props;

  const styles = useStyles();
  const { admin, redesign } = useAdminColors();
  const { showFullTable, layoutMode } = useResponsive();
  const table = useDataTableState();

  const searchQuery = searchQueryProp ?? table.searchQuery;
  const onSearchChange = onSearchChangeProp ?? table.setSearch;
  const filterModel = filterModelProp ?? table.filterModel;
  const onFilterChange = onFilterChangeProp ?? table.setFilter;
  const sortModel = sortModelProp ?? table.sortModel;
  const onSortChange = onSortChangeProp ?? table.setSort;
  const paginationModel = paginationModelProp ?? table.paginationModel;
  const onPaginationChange = onPaginationChangeProp ?? table.setPagination;

  const hasNextPage = hasNextPageProp ?? (
    (paginationModel.page + 1) * paginationModel.pageSize < data.count
  );
  const hasPreviousPage = hasPreviousPageProp ?? (paginationModel.page > 0);
  const onNextPage = onNextPageProp ?? (() => {
    if (hasNextPage) {
      onPaginationChange({ ...paginationModel, page: paginationModel.page + 1 });
    }
  });
  const onPreviousPage = onPreviousPageProp ?? (() => {
    if (hasPreviousPage) {
      onPaginationChange({ ...paginationModel, page: paginationModel.page - 1 });
    }
  });

  // Use desktop table for desktop layout mode
  if (layoutMode === 'desktop' && showFullTable) {
    return (
      <DesktopDataTable
        {...(props as any)}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        filterModel={filterModel}
        onFilterChange={onFilterChange}
        sortModel={sortModel}
        onSortChange={onSortChange}
        paginationModel={paginationModel}
        onPaginationChange={onPaginationChange}
        hasNextPage={hasNextPage}
        hasPreviousPage={hasPreviousPage}
        onNextPage={onNextPage}
        onPreviousPage={onPreviousPage}
        showSort={showSort}
      />
    );
  }

  useEffect(() => {
    if (data.rows.length > 0 && updateCsvData) {
      updateCsvData(data.rows);
    }
  }, [data.rows, updateCsvData]);

  const renderRow = (item: T, index: number) => {
    const isSelected = enableRowSelection && selectedRows.some((selectedRow) =>
      (selectedRow as any).id === (item as any).id,
    );

    const rowId = (item as any)?.id ?? index;
    return (
      <TableRow
        key={rowId}
        row={item}
        columns={columns}
        index={index}
        onPress={onRowSelect}
        isTabletView={isTabletView}
        mobileColumnCount={mobileColumnCount}
        isSelected={isSelected}
        enableSelection={enableRowSelection}
      />
    );
  };

  const renderFlatListItem: ListRenderItem<T> = ({ item, index }) => renderRow(item, index);
  const flatListKeyExtractor = (item: T, index: number) => String((item as any)?.id ?? index);

  const renderEmptyState = () => {
    const hasActiveSearch = searchQuery && searchQuery.trim() !== '';
    const hasActiveFilters = filterModel && filterModel.items && filterModel.items.length > 0;
    const hasActiveSearchOrFilters = hasActiveSearch || hasActiveFilters;

    return (
      <View style={styles.emptyStateContainer}>
        {hasActiveSearchOrFilters ? (
          <View style={styles.noResultsContainer}>
            <Text style={styles.noResultsTitle}>No results found</Text>
            <Text style={styles.noResultsMessage}>
              {hasActiveSearch && hasActiveFilters
                ? `No results match your search "${searchQuery}" and current filters. Try adjusting your search or filters.`
                : hasActiveSearch
                  ? `No results match your search "${searchQuery}". Try a different search term.`
                  : 'No results match your current filters. Try adjusting your filters.'}
            </Text>
          </View>
        ) : (
          <AdminEmptyState type={emptyStateType as EmptyStateType} />
        )}
      </View>
    );
  };

  const renderLoadingState = () => (
    <View style={styles.loadingContainer}>
      <ActivityIndicator size="large" color={admin.formInput.borderFocused} />
      <Text style={styles.loadingText}>Loading...</Text>
    </View>
  );

  const renderPaginationControls = () => {
    const startIndex = paginationModel.page * paginationModel.pageSize + 1;
    const endIndex = Math.min((paginationModel.page + 1) * paginationModel.pageSize, data.count);

    return (
      <View style={styles.paginationContainer}>
        <Text style={styles.paginationText}>
          {startIndex}–{endIndex} of {data.count}
        </Text>

        <View style={styles.paginationButtons}>
          <TouchableOpacity
            style={[styles.paginationButton, !hasPreviousPage && styles.paginationButtonDisabled]}
            onPress={onPreviousPage}
            disabled={!hasPreviousPage}
          >
            <SVG_COMPONENTS.CHEVRON_LEFT
              width={16}
              height={16}
              color={!hasPreviousPage ? redesign.text.dim : admin.dataTable.text}
            />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.paginationButton, !hasNextPage && styles.paginationButtonDisabled]}
            onPress={onNextPage}
            disabled={!hasNextPage}
          >
            <SVG_COMPONENTS.CHEVRON_RIGHT
              width={16}
              height={16}
              color={!hasNextPage ? redesign.text.dim : admin.dataTable.text}
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  if (isLoading && data.rows.length === 0) {
    return (
      <View style={[styles.container, containerStyle]}>
        <TableFilters
          columns={columns}
          searchQuery={searchQuery}
          onSearchChange={showSearch ? onSearchChange : undefined}
          searchPlaceholder={searchPlaceholder}
          showSearch={showSearch}
          filterModel={filterModel}
          onFilterChange={onFilterChange}
          sortModel={sortModel}
          onSortChange={onSortChange}
        />
        {renderLoadingState()}
      </View>
    );
  }

  return (
    <View style={[styles.container, containerStyle]}>
      <TableFilters
        columns={columns}
        searchQuery={searchQuery}
        onSearchChange={showSearch ? onSearchChange : undefined}
        searchPlaceholder={searchPlaceholder}
        showSearch={showSearch}
        filterModel={filterModel}
        onFilterChange={onFilterChange}
        sortModel={sortModel}
        onSortChange={onSortChange}
        enableRowSelection={enableRowSelection}
        selectedRows={selectedRows}
        totalRows={data.rows.length}
        onSelectAll={onSelectAll}
        onDeselectAll={onDeselectAll}
      />

      {isLoading && data.rows.length > 0 && (
        <View style={styles.refreshIndicator}>
          <ActivityIndicator size="small" color={admin.formInput.borderFocused} />
        </View>
      )}

      <View style={styles.list}>
        {data.rows.length === 0 && !isLoading ? (
          renderEmptyState()
        ) : (
          <FlatList
            data={data.rows}
            renderItem={renderFlatListItem}
            keyExtractor={flatListKeyExtractor}
            ListFooterComponent={data.rows.length > 0 ? renderPaginationControls() : null}
            initialNumToRender={25}
            maxToRenderPerBatch={25}
            windowSize={10}
            updateCellsBatchingPeriod={50}
            showsVerticalScrollIndicator={false}
          />
        )}
      </View>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        flex: 1,
        backgroundColor: 'transparent',
        width: '100%',
      },
      list: {
        flex: 1,
      },
      refreshIndicator: {
        position: 'absolute',
        top: 10,
        right: 16,
        zIndex: 10,
        backgroundColor: admin.dataTable.headerBg,
        borderColor: admin.dataTable.border,
        borderWidth: 1,
        borderRadius: 12,
        padding: 4,
      },
      emptyStateContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 40,
      },
      noResultsContainer: {
        alignItems: 'center',
        paddingHorizontal: 20,
        marginTop: -120,
        maxWidth: 300,
      },
      noResultsTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: admin.dataTable.text,
        marginBottom: 8,
        textAlign: 'center',
      },
      noResultsMessage: {
        fontSize: 14,
        color: colors.text.dim,
        textAlign: 'center',
        lineHeight: 20,
      },
      loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 40,
      },
      loadingText: {
        marginTop: 12,
        fontSize: 16,
        color: colors.text.dim,
      },
      paginationContainer: {
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
        backgroundColor: 'transparent',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      paginationText: {
        fontSize: 14,
        color: colors.text.dim,
      },
      paginationButtons: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
      },
      paginationButton: {
        padding: 8,
        alignItems: 'center',
        justifyContent: 'center',
      },
      paginationButtonDisabled: {
        // Styling handled by icon color
      },
    });
  });
